import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aid-center-17/artifacts/xzfmjjft_The%20Fan%20Rant%20Line.png";

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-brand-black">
      {/* Header */}
      <header className="bg-black/95 border-b-4 border-brand-blue py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="The Fan Rant Line" className="h-10" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors font-chivo"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="font-anton text-4xl md:text-5xl text-white mb-2">
          TERMS OF <span className="text-brand-blue">SERVICE</span>
        </h1>
        <p className="font-mono text-sm text-gray-500 mb-8">Last Updated: January 2026</p>

        <div className="space-y-8 font-chivo text-gray-300 leading-relaxed">
          
          {/* Section 1 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">1. ACCEPTANCE OF TERMS</h2>
            <p>
              Welcome to The Fan Rant Line ("we," "our," or "us"). By accessing or using our website 
              at thefanrantline.com (the "Site") and any related services, you agree to be bound by 
              these Terms of Service ("Terms"). If you do not agree to these Terms, please do not 
              use our Site or services.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">2. USER-GENERATED CONTENT</h2>
            <p className="mb-4">
              Our Site allows users to submit text-based rants and voice recordings ("User Content"). 
              By submitting any User Content to The Fan Rant Line, you:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Confirm that you are at least 13 years of age</li>
              <li>Confirm that the content is your own original work or you have the right to submit it</li>
              <li>Understand that your submission may be publicly displayed on our Site</li>
              <li>Accept full responsibility for the content you submit</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">3. LICENSE TO USE YOUR CONTENT</h2>
            <p className="mb-4">
              By submitting User Content to The Fan Rant Line, you grant us a non-exclusive, 
              worldwide, royalty-free, perpetual, irrevocable, and fully sublicensable license to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use, reproduce, modify, adapt, publish, and display your User Content</li>
              <li>Distribute your User Content across our website, social media platforms (including but not limited to Instagram, TikTok, X/Twitter, YouTube, and Facebook), podcasts, and any other media formats</li>
              <li>Create derivative works from your User Content</li>
              <li>Use your User Content for marketing, promotional, and commercial purposes</li>
              <li>Sublicense these rights to third parties</li>
            </ul>
            <p className="mt-4">
              You waive any rights of attribution and any moral rights you may have in your User Content. 
              All submissions become the property of The Fan Rant Line.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">4. PROHIBITED CONTENT</h2>
            <p className="mb-4">
              You agree NOT to submit content that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Contains hate speech, threats, harassment, or discriminatory language</li>
              <li>Includes personal information about others without their consent</li>
              <li>Is defamatory, obscene, or otherwise objectionable</li>
              <li>Infringes on any third party's intellectual property rights</li>
              <li>Contains spam, advertising, or promotional material</li>
              <li>Violates any applicable laws or regulations</li>
              <li>Impersonates any person or entity</li>
            </ul>
            <p className="mt-4">
              We reserve the right to remove any content that violates these Terms at our sole discretion, 
              without notice or liability.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">5. CONTENT MODERATION</h2>
            <p>
              The Fan Rant Line reserves the right, but not the obligation, to monitor, edit, or remove 
              any User Content at any time and for any reason without notice. We are not responsible for 
              any User Content posted by users. The views expressed in User Content are those of the 
              individual users and do not reflect the views of The Fan Rant Line.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">6. INTELLECTUAL PROPERTY</h2>
            <p>
              The Fan Rant Line name, logo, website design, and all related graphics, icons, and service 
              names are trademarks of The Fan Rant Line. You may not use these without our prior written 
              permission. All other trademarks, product names, and company names or logos mentioned on 
              the Site are the property of their respective owners.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">7. DISCLAIMER OF WARRANTIES</h2>
            <p>
              THE SITE AND ALL CONTENT ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR 
              IMPLIED. WE DO NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF 
              VIRUSES OR OTHER HARMFUL COMPONENTS. YOUR USE OF THE SITE IS AT YOUR OWN RISK.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">8. LIMITATION OF LIABILITY</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY LAW, THE FAN RANT LINE SHALL NOT BE LIABLE FOR ANY 
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS 
              OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, 
              OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF THE SITE.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">9. INDEMNIFICATION</h2>
            <p>
              You agree to indemnify, defend, and hold harmless The Fan Rant Line and its owners, 
              employees, and affiliates from any claims, damages, losses, liabilities, and expenses 
              (including attorney's fees) arising out of or related to your User Content, your use 
              of the Site, or your violation of these Terms.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">10. PRIVACY</h2>
            <p>
              We collect certain information when you use our Site, including your name (or username) 
              and the content you submit. We may use cookies and similar technologies to improve your 
              experience. By using our Site, you consent to our collection and use of information as 
              described here. We do not sell your personal information to third parties.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">11. CHANGES TO TERMS</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of any 
              material changes by posting the updated Terms on this page with a new "Last Updated" 
              date. Your continued use of the Site after any changes constitutes your acceptance of 
              the new Terms.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">12. GOVERNING LAW</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the 
              United States, without regard to its conflict of law provisions.
            </p>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="font-anton text-2xl text-white mb-4">13. CONTACT US</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us through our 
              social media channels or website.
            </p>
            <div className="mt-4 p-4 bg-brand-surface border-2 border-white/20">
              <p className="font-anton text-xl text-brand-blue">The Fan Rant Line</p>
              <p className="text-gray-400">Website: thefanrantline.com</p>
              <p className="text-gray-400">X/Twitter: @TheFanRantLine</p>
              <p className="text-gray-400">Instagram: @thefanrantline</p>
            </div>
          </section>

        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <Link 
            to="/"
            className="btn-brutal inline-flex items-center gap-3 bg-brand-blue text-white font-anton text-xl px-6 py-3 border-2 border-white shadow-brutal hover:shadow-brutal-hover"
          >
            <ArrowLeft size={24} />
            BACK TO HOME
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black border-t-4 border-brand-blue py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="font-chivo text-gray-500">
            © {new Date().getFullYear()} The Fan Rant Line - Your Voice in Sports Commentary
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
